import { useState, useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

// ─── Types ────────────────────────────────────────────────────────────────────
const ItemTypes = {
  CARD: 'CARD',
  COLUMN: 'COLUMN',
};

// ─── Initial State ─────────────────────────────────────────────────────────────
const initialData = {
  columns: {
    col1: { id: 'col1', title: 'Backlog', taskIds: ['t1', 't2', 't3'] },
    col2: { id: 'col2', title: 'En progreso', taskIds: ['t4', 't5'] },
    col3: { id: 'col3', title: 'Revisión', taskIds: ['t6'] },
    col4: { id: 'col4', title: 'Completado', taskIds: [] as string[] },
  } as Record<string, { id: string; title: string; taskIds: string[] }>,
  columnOrder: ['col1', 'col2', 'col3', 'col4'],
  tasks: {
    t1: {
      id: 't1',
      title: 'Diseñar wireframes',
      children: ['t1a', 't1b'],
      parentId: undefined as string | undefined,
    },
    t1a: { id: 't1a', title: 'Homepage', children: ['t1a1'], parentId: 't1' },
    t1a1: { id: 't1a1', title: 'Hero section', children: [] as string[], parentId: 't1a' },
    t1b: { id: 't1b', title: 'Dashboard', children: [] as string[], parentId: 't1' },
    t2: {
      id: 't2',
      title: 'Setup repositorio',
      children: [] as string[],
      parentId: undefined as string | undefined,
    },
    t3: {
      id: 't3',
      title: 'Definir arquitectura',
      children: ['t3a'],
      parentId: undefined as string | undefined,
    },
    t3a: { id: 't3a', title: 'Base de datos', children: [] as string[], parentId: 't3' },
    t4: {
      id: 't4',
      title: 'Implementar auth',
      children: ['t4a', 't4b'],
      parentId: undefined as string | undefined,
    },
    t4a: { id: 't4a', title: 'Login con Google', children: [] as string[], parentId: 't4' },
    t4b: { id: 't4b', title: 'JWT tokens', children: [] as string[], parentId: 't4' },
    t5: {
      id: 't5',
      title: 'API endpoints',
      children: [] as string[],
      parentId: undefined as string | undefined,
    },
    t6: {
      id: 't6',
      title: 'Code review PR #12',
      children: [] as string[],
      parentId: undefined as string | undefined,
    },
  } as Record<string, { id: string; title: string; children: string[]; parentId?: string }>,
};

type BoardData = typeof initialData;

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Encuentra en qué columna vive una task (busca solo en nivel raíz de columnas)
function findColumnOfTask(taskId: string, data: BoardData)
{
  for (const col of Object.values(data.columns))
  {
    if (col.taskIds.includes(taskId)) return col.id;
  }
  return null;
}

// Obtiene todos los descendientes de una task (para evitar drops en sí mismo)
function getAllDescendants(taskId: string, tasks: BoardData['tasks'])
{
  const result: string[] = [];
  const stack = [...(tasks[taskId]?.children || [])];
  while (stack.length)
  {
    const id = stack.pop()!;
    result.push(id);
    stack.push(...(tasks[id]?.children || []));
  }
  return result;
}

// Genera ID único
let _id = 100;
const genId = (prefix = 't') => `${prefix}${++_id}`;

// ─── Card Component (recursivo) ───────────────────────────────────────────────
function Card({
  taskId,
  data,
  setData,
  depth = 0,
}: {
  taskId: string;
  data: BoardData;
  setData: React.Dispatch<React.SetStateAction<BoardData>>;
  depth?: number;
})
{
  const [expanded, setExpanded] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: () => ({ id: taskId, type: ItemTypes.CARD }),
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.CARD,
    canDrop: (item: { id: string }) =>
    {
      if (item.id === taskId) return false;
      const descendants = getAllDescendants(taskId, data.tasks);
      if (descendants.includes(item.id)) return false;
      return true;
    },
    drop: (item: { id: string }, monitor) =>
    {
      if (monitor.didDrop()) return; // ya fue manejado por un hijo
      moveCardAsChild(item.id, taskId);
    },
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  drag(drop(ref));

  const moveCardAsChild = useCallback(
    (dragId: string, targetParentId: string) =>
    {
      setData(prev =>
      {
        const next = JSON.parse(JSON.stringify(prev)) as BoardData;
        const dragTask = next.tasks[dragId];
        if (!dragTask) return prev;

        // Remover de columna si está en nivel raíz
        const colId = findColumnOfTask(dragId, next);
        if (colId && next.columns[colId])
        {
          next.columns[colId].taskIds = next.columns[colId].taskIds.filter(id => id !== dragId);
        }

        // Remover del padre anterior si tiene
        if (dragTask.parentId)
        {
          const oldParent = next.tasks[dragTask.parentId];
          if (oldParent)
          {
            oldParent.children = oldParent.children.filter(id => id !== dragId);
          }
        }

        // Agregar como hijo del nuevo padre
        dragTask.parentId = targetParentId;
        const targetParent = next.tasks[targetParentId];
        if (targetParent && !targetParent.children.includes(dragId))
        {
          targetParent.children.push(dragId);
        }

        return next;
      });
    },
    [setData],
  );

  const task = data.tasks[taskId];
  if (!task) return null;

  const addChild = () =>
  {
    if (!newTitle.trim()) return;
    const id = genId();
    setData(prev =>
    {
      const next = JSON.parse(JSON.stringify(prev)) as BoardData;
      next.tasks[id] = { id, title: newTitle.trim(), children: [], parentId: taskId };
      next.tasks[taskId]?.children.push(id);
      return next;
    });
    setNewTitle('');
    setAdding(false);
    setExpanded(true);
  };

  const deleteTask = () =>
  {
    setData(prev =>
    {
      const next = JSON.parse(JSON.stringify(prev)) as BoardData;
      // Quitar de columna raíz
      const colId = findColumnOfTask(taskId, next);
      if (colId && next.columns[colId])
      {
        next.columns[colId].taskIds = next.columns[colId].taskIds.filter(id => id !== taskId);
      }
      // Quitar de padre
      if (task?.parentId)
      {
        const parent = next.tasks[task.parentId];
        if (parent)
        {
          parent.children = parent.children.filter(id => id !== taskId);
        }
      }
      // Eliminar task y descendientes
      const toDelete = [taskId, ...getAllDescendants(taskId, next.tasks)];
      toDelete.forEach(id => delete next.tasks[id]);
      return next;
    });
  };

  const hasChildren = (task?.children.length ?? 0) > 0;
  const isDropTarget = isOver && canDrop;

  return (
    <div
      style={{
        marginLeft: depth > 0 ? 16 : 0,
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      <div
        ref={ref}
        style={{
          background: isDropTarget
            ? 'rgba(139, 92, 246, 0.15)'
            : depth === 0
              ? '#1e1e2e'
              : depth === 1
                ? '#181825'
                : '#13131f',
          border: isDropTarget
            ? '1.5px dashed #8b5cf6'
            : depth === 0
              ? '1px solid #313244'
              : '1px solid #27273a',
          borderRadius: 10,
          padding: '10px 12px',
          marginBottom: 6,
          cursor: 'grab',
          userSelect: 'none',
          boxShadow: isDragging
            ? '0 8px 24px rgba(0,0,0,0.4)'
            : depth === 0
              ? '0 2px 8px rgba(0,0,0,0.3)'
              : 'none',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Expand toggle */}
          {hasChildren && (
            <button
              onClick={() => setExpanded(v => !v)}
              style={{
                background: 'none',
                border: 'none',
                color: '#7c7c9c',
                cursor: 'pointer',
                padding: '0 2px',
                fontSize: 10,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {expanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <span style={{ width: 14, flexShrink: 0 }} />}

          {/* Drag handle */}
          <span style={{ color: '#45455a', fontSize: 12, flexShrink: 0 }}>⠿</span>

          {/* Depth indicator dots */}
          {depth > 0 && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: depth === 1 ? '#8b5cf6' : depth === 2 ? '#06b6d4' : '#f59e0b',
                flexShrink: 0,
              }}
            />
          )}

          <span
            style={{
              flex: 1,
              color: '#cdd6f4',
              fontSize: depth === 0 ? 14 : 13,
              fontWeight: depth === 0 ? 500 : 400,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {task?.title}
          </span>

          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button
              onClick={() => setAdding(v => !v)}
              title="Agregar subtarea"
              style={{
                background: 'none',
                border: 'none',
                color: '#7c7c9c',
                cursor: 'pointer',
                fontSize: 13,
                padding: '0 3px',
                lineHeight: 1,
              }}
            >
              +
            </button>
            <button
              onClick={deleteTask}
              title="Eliminar"
              style={{
                background: 'none',
                border: 'none',
                color: '#45455a',
                cursor: 'pointer',
                fontSize: 11,
                padding: '0 3px',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Add child form */}
        {adding && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
            <input
              autoFocus
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e =>
              {
                if (e.key === 'Enter') addChild();
                if (e.key === 'Escape') setAdding(false);
              }}
              placeholder="Nombre de subtarea..."
              style={{
                flex: 1,
                background: '#11111b',
                border: '1px solid #313244',
                borderRadius: 6,
                padding: '5px 8px',
                color: '#cdd6f4',
                fontSize: 12,
                fontFamily: "'DM Sans', sans-serif",
                outline: 'none',
              }}
            />
            <button
              onClick={addChild}
              style={{
                background: '#8b5cf6',
                border: 'none',
                borderRadius: 6,
                color: 'white',
                padding: '5px 10px',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Ok
            </button>
          </div>
        )}
      </div>

      {/* Children (recursive) */}
      {hasChildren && expanded && (
        <div style={{ borderLeft: '1px solid #27273a', marginLeft: 10, paddingLeft: 6 }}>
          {task?.children.map(childId => (
            <Card key={childId} taskId={childId} data={data} setData={setData} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Column Drop Zone (para soltar tarjetas en columna) ───────────────────────
function ColumnDropZone({
  columnId,
  setData,
}: {
  columnId: string;
  setData: React.Dispatch<React.SetStateAction<BoardData>>;
})
{
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.CARD,
    drop: (item: { id: string }, monitor) =>
    {
      if (monitor.didDrop()) return;
      moveToColumn(item.id, columnId);
    },
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  const moveToColumn = (dragId: string, targetColId: string) =>
  {
    setData(prev =>
    {
      const next = JSON.parse(JSON.stringify(prev)) as BoardData;
      const dragTask = next.tasks[dragId];
      if (!dragTask) return prev;

      // Remover de columna anterior
      const oldColId = findColumnOfTask(dragId, next);
      if (oldColId && next.columns[oldColId])
      {
        next.columns[oldColId].taskIds = next.columns[oldColId].taskIds.filter(id => id !== dragId);
      }

      // Remover de padre si tenía
      if (dragTask.parentId)
      {
        const oldParent = next.tasks[dragTask.parentId];
        if (oldParent)
        {
          oldParent.children = oldParent.children.filter(id => id !== dragId);
        }
        dragTask.parentId = undefined;
      }

      // Agregar a la nueva columna
      const targetCol = next.columns[targetColId];
      if (targetCol && !targetCol.taskIds.includes(dragId))
      {
        targetCol.taskIds.push(dragId);
      }

      return next;
    });
  };

  return (
    <div
      ref={drop as unknown as React.Ref<HTMLDivElement>}
      style={{
        minHeight: 48,
        borderRadius: 8,
        border: isOver && canDrop ? '1.5px dashed #8b5cf6' : '1.5px dashed transparent',
        background: isOver && canDrop ? 'rgba(139,92,246,0.06)' : 'transparent',
        marginTop: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s',
      }}
    >
      {isOver && canDrop && (
        <span style={{ color: '#8b5cf6', fontSize: 12, opacity: 0.7 }}>Soltar aquí</span>
      )}
    </div>
  );
}

// ─── Column Component ─────────────────────────────────────────────────────────
function Column({
  columnId,
  index,
  data,
  setData,
  moveColumn,
}: {
  columnId: string;
  index: number;
  data: BoardData;
  setData: React.Dispatch<React.SetStateAction<BoardData>>;
  moveColumn: (dragId: string, hoverId: string) => void;
})
{
  const ref = useRef<HTMLDivElement>(null);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.COLUMN,
    item: { id: columnId, index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.COLUMN,
    hover: (item: { id: string; index: number }) =>
    {
      if (item.id === columnId) return;
      moveColumn(item.id, columnId);
      item.index = index;
    },
    collect: monitor => ({ isOver: monitor.isOver() }),
  });

  drag(drop(ref));

  const column = data.columns[columnId];
  if (!column) return null;

  const addCard = () =>
  {
    if (!newTitle.trim()) return;
    const id = genId();
    setData(prev =>
    {
      const next = JSON.parse(JSON.stringify(prev)) as BoardData;
      next.tasks[id] = { id, title: newTitle.trim(), children: [] };
      next.columns[columnId]?.taskIds.push(id);
      return next;
    });
    setNewTitle('');
    setAdding(false);
  };

  const deleteColumn = () =>
  {
    setData(prev =>
    {
      const next = JSON.parse(JSON.stringify(prev)) as BoardData;
      // Eliminar todas las tasks de la columna y sus descendientes
      const rootIds = next.columns[columnId]?.taskIds ?? [];
      rootIds.forEach(taskId =>
      {
        const toDelete = [taskId, ...getAllDescendants(taskId, next.tasks)];
        toDelete.forEach(id => delete next.tasks[id]);
      });
      delete next.columns[columnId];
      next.columnOrder = next.columnOrder.filter(id => id !== columnId);
      return next;
    });
  };

  return (
    <div
      ref={ref}
      style={{
        width: 280,
        flexShrink: 0,
        background: isDragging ? 'rgba(24,24,37,0.5)' : '#181825',
        border: isOver ? '1.5px solid #8b5cf6' : '1px solid #27273a',
        borderRadius: 14,
        padding: '14px 12px 12px',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        transition: 'all 0.15s',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      }}
    >
      {/* Column Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          paddingBottom: 10,
          borderBottom: '1px solid #27273a',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#45455a', fontSize: 14 }}>⠿</span>
          <span
            style={{
              color: '#cdd6f4',
              fontWeight: 600,
              fontSize: 14,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {column.title}
          </span>
          <span
            style={{
              background: '#313244',
              color: '#7c7c9c',
              borderRadius: 20,
              padding: '1px 7px',
              fontSize: 11,
            }}
          >
            {column.taskIds.length}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => setAdding(v => !v)}
            style={{
              background: 'none',
              border: 'none',
              color: '#7c7c9c',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: '0 4px',
            }}
          >
            +
          </button>
          <button
            onClick={deleteColumn}
            style={{
              background: 'none',
              border: 'none',
              color: '#45455a',
              cursor: 'pointer',
              fontSize: 12,
              padding: '0 4px',
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Add card form */}
      {adding && (
        <div style={{ marginBottom: 10 }}>
          <input
            autoFocus
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e =>
            {
              if (e.key === 'Enter') addCard();
              if (e.key === 'Escape') setAdding(false);
            }}
            placeholder="Nombre de tarea..."
            style={{
              width: '100%',
              background: '#11111b',
              border: '1px solid #8b5cf6',
              borderRadius: 8,
              padding: '7px 10px',
              color: '#cdd6f4',
              fontSize: 13,
              fontFamily: "'DM Sans', sans-serif",
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button
              onClick={addCard}
              style={{
                flex: 1,
                background: '#8b5cf6',
                border: 'none',
                borderRadius: 7,
                color: 'white',
                padding: '6px',
                cursor: 'pointer',
                fontSize: 12,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Agregar
            </button>
            <button
              onClick={() => setAdding(false)}
              style={{
                background: '#313244',
                border: 'none',
                borderRadius: 7,
                color: '#7c7c9c',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Cards */}
      <div style={{ minHeight: 20 }}>
        {column.taskIds.map(taskId => (
          <Card key={taskId} taskId={taskId} data={data} setData={setData} depth={0} />
        ))}
      </div>

      {/* Drop zone at bottom of column */}
      <ColumnDropZone columnId={columnId} setData={setData} />
    </div>
  );
}

// ─── Board ─────────────────────────────────────────────────────────────────────
function Board()
{
  const [data, setData] = useState(initialData);
  const [addingCol, setAddingCol] = useState(false);
  const [newColTitle, setNewColTitle] = useState('');

  const moveColumn = useCallback((dragId: string, hoverId: string) =>
  {
    setData(prev =>
    {
      const next = JSON.parse(JSON.stringify(prev)) as BoardData;
      const order = next.columnOrder;
      const fromIdx = order.indexOf(dragId);
      const toIdx = order.indexOf(hoverId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      order.splice(fromIdx, 1);
      order.splice(toIdx, 0, dragId);
      return next;
    });
  }, []);

  const addColumn = () =>
  {
    if (!newColTitle.trim()) return;
    const id = genId('col');
    setData(prev =>
    {
      const next = JSON.parse(JSON.stringify(prev)) as BoardData;
      next.columns[id] = { id, title: newColTitle.trim(), taskIds: [] };
      next.columnOrder.push(id);
      return next;
    });
    setNewColTitle('');
    setAddingCol(false);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#11111b',
        padding: '32px 24px',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            color: '#cdd6f4',
            fontSize: 22,
            fontWeight: 700,
            margin: 0,
            letterSpacing: '-0.5px',
          }}
        >
          Tablero
        </h1>
        <p style={{ color: '#45455a', margin: '4px 0 0', fontSize: 13 }}>
          Arrastra columnas para reordenar · Arrastra tarjetas entre columnas o sobre otras tarjetas
          para anidar
        </p>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { color: '#8b5cf6', label: 'Nivel 1' },
          { color: '#06b6d4', label: 'Nivel 2' },
          { color: '#f59e0b', label: 'Nivel 3+' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: color,
                display: 'inline-block',
              }}
            />
            <span style={{ color: '#45455a', fontSize: 12 }}>{label}</span>
          </div>
        ))}
        <span style={{ color: '#45455a', fontSize: 12, marginLeft: 8 }}>
          · Haz <strong style={{ color: '#7c7c9c' }}>+</strong> en una tarjeta para agregar subtarea
        </span>
      </div>

      {/* Columns */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          overflowX: 'auto',
          paddingBottom: 16,
        }}
      >
        {data.columnOrder.map((colId, idx) => (
          <Column
            key={colId}
            columnId={colId}
            index={idx}
            data={data}
            setData={setData}
            moveColumn={moveColumn}
          />
        ))}

        {/* Add column */}
        <div style={{ flexShrink: 0 }}>
          {!addingCol ? (
            <button
              onClick={() => setAddingCol(true)}
              style={{
                width: 220,
                background: 'rgba(49,50,68,0.4)',
                border: '1px dashed #313244',
                borderRadius: 14,
                color: '#45455a',
                padding: '14px',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all 0.15s',
              }}
              onMouseEnter={e =>
              {
                (e.target as HTMLButtonElement).style.borderColor = '#8b5cf6';
                (e.target as HTMLButtonElement).style.color = '#8b5cf6';
              }}
              onMouseLeave={e =>
              {
                (e.target as HTMLButtonElement).style.borderColor = '#313244';
                (e.target as HTMLButtonElement).style.color = '#45455a';
              }}
            >
              + Nueva columna
            </button>
          ) : (
            <div
              style={{
                width: 220,
                background: '#181825',
                border: '1px solid #8b5cf6',
                borderRadius: 14,
                padding: 14,
              }}
            >
              <input
                autoFocus
                value={newColTitle}
                onChange={e => setNewColTitle(e.target.value)}
                onKeyDown={e =>
                {
                  if (e.key === 'Enter') addColumn();
                  if (e.key === 'Escape') setAddingCol(false);
                }}
                placeholder="Nombre de columna..."
                style={{
                  width: '100%',
                  background: '#11111b',
                  border: '1px solid #313244',
                  borderRadius: 8,
                  padding: '7px 10px',
                  color: '#cdd6f4',
                  fontSize: 13,
                  fontFamily: "'DM Sans', sans-serif",
                  outline: 'none',
                  boxSizing: 'border-box',
                  marginBottom: 8,
                }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={addColumn}
                  style={{
                    flex: 1,
                    background: '#8b5cf6',
                    border: 'none',
                    borderRadius: 7,
                    color: 'white',
                    padding: '6px',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Crear
                </button>
                <button
                  onClick={() => setAddingCol(false)}
                  style={{
                    background: '#313244',
                    border: 'none',
                    borderRadius: 7,
                    color: '#7c7c9c',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────
export default function DndTestPage()
{
  return (
    <Board />
  );
}
